import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

// Supporting video embed for glossary terms and docs topics.
//
// This component deliberately emits NO VideoObject. Google judged every
// article page carrying one as "Video isn't on a watch page: the video is
// supplementary content on the page" — which is an accurate reading of these
// templates. The schema, the video sitemap entry, and the canonical location
// for each video now live on its watch page at /videos/<slug> (see
// src/pages/VideoWatchPage.js and buildVideoObject in
// scripts/generate-snap-routes.js). What stays here is the player, for
// readers, plus a link to the watch page so the two are connected.
const VideoEmbed = ({ video }) => {
  if (!video || !video.youtubeId) return null;

  const { youtubeId, title, description, transcript, slug } = video;
  const embedUrl = `https://www.youtube.com/embed/${youtubeId}?rel=0`;

  return (
    <div className="my-10">
      <div
        className="relative w-full overflow-hidden rounded-2xl border border-apple-700/40 shadow-lg"
        style={{ paddingTop: '56.25%' }}
      >
        {/* No loading="lazy": a lazy iframe the renderer never loads cannot be
            indexed, and one player per page makes eager loading cheap. */}
        <iframe
          className="absolute inset-0 h-full w-full"
          src={embedUrl}
          title={title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
      {description && (
        <p className="mt-3 text-sm text-apple-300">{description}</p>
      )}
      {slug && (
        <Link
          to={`/videos/${slug}`}
          className="mt-3 inline-flex items-center text-sm text-apple-400 hover:text-sortio-primary transition-colors group"
        >
          Open this video on its own page
          <ArrowRight className="ml-1 group-hover:translate-x-0.5 transition-transform" size={14} />
        </Link>
      )}
      {transcript && (
        <details className="mt-4 rounded-xl border border-apple-700/40 bg-apple-800/30 p-4">
          <summary className="cursor-pointer text-sm font-medium text-apple-100">
            Video transcript
          </summary>
          <div className="mt-3 space-y-3 text-sm leading-relaxed text-apple-300">
            {transcript.split('\n\n').map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

export default VideoEmbed;
